import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

type ThemeName = 'dark' | 'light' | undefined | null;

@Injectable({ providedIn: 'root' })
export class ThemeService implements OnDestroy {
  private themeKey = 'theme';
  themeValue = new BehaviorSubject<'dark' | 'light'>(this.getInitialTheme());

  constructor(@Inject(DOCUMENT) private document: Document) {}

  private getInitialTheme(): 'dark' | 'light' {
    const themeVal = localStorage.getItem(this.themeKey);
    return themeVal === 'dark' ? 'dark' : 'light';
  }

  set theme(value: ThemeName) {
    const normalizedValue = value || 'light';
    
    // Update BehaviorSubject first
    this.themeValue.next(normalizedValue);
    
    // Update localStorage
    localStorage.setItem(this.themeKey, normalizedValue);
    
    // Update current theme styling
    this.updateCurrentTheme();
    
    // Dispatch custom event to notify the index.html script
    this.dispatchThemeChangeEvent(normalizedValue);
    
    console.log('ThemeService: theme set to', normalizedValue);
  }

  get theme(): ThemeName {
    const themeVal = localStorage.getItem(this.themeKey);
    if (themeVal === 'dark') {
      return 'dark';
    } else {
      return 'light';
    }
  }

  private dispatchThemeChangeEvent(theme: ThemeName): void {
    // Dispatch a custom event to notify the index.html script
    const event = new CustomEvent('themeChange', {
      detail: { theme: theme || 'light' }
    })
    window.dispatchEvent(event)
  }

  updateCurrentTheme() {
    this.loadTelerikStyle(this.theme);
  }

  loadTelerikStyle(themeName: ThemeName) {
    const styleName =
      themeName === 'dark' ? 'dark-theme.css' : 'light-theme.css';
    const head = this.document.getElementsByTagName('head')[0];

    const themeLink = this.getTelerikThemeLink();
    if (themeLink) {
      themeLink.href = styleName;
    } else {
      const style = this.document.createElement('link');
      style.id = 'client-theme';
      style.rel = 'stylesheet';
      style.href = `${styleName}`;

      head.appendChild(style);
    }
  }

  getTelerikThemeLink() {
    return this.document.getElementById('client-theme') as HTMLLinkElement;
  }

  ngOnDestroy() {
    localStorage.removeItem(this.themeKey);
    const themeLink = this.getTelerikThemeLink();

    if (themeLink) {
      themeLink.remove();
    }
  }
}
